import axios, { AxiosInstance } from 'axios';
import { API_CONFIG, isMockMode } from '../config/api.config';
import {
  AuthResponse,
  LoginRequest,
  SendOtpRequest,
  OtpResponse,
  Trip,
  CreateTripData,
  UpdateTripData,
  Checklist,
  CreateChecklistData,
  Expense,
  CreateExpenseData,
  Announcement,
  Location,
  Notification,
  DailyPlan,
  Document,
} from '../types';
import { getMockTrips } from '../data/mock/trips.mock';
import {
  addMockTripMember,
  createMockAnnouncement,
  createMockChecklist,
  createMockDailyPlan,
  createMockDocument,
  createMockExpense,
  createMockLocation,
  createMockTrip,
  deleteMockAnnouncement,
  deleteMockChecklist,
  deleteMockDailyPlan,
  deleteMockDocument,
  deleteMockExpense,
  deleteMockLocation,
  deleteMockTrip,
  getMockAnnouncements,
  getMockChecklists,
  getMockDailyPlans,
  getMockDocuments,
  getMockExpenses,
  getMockLocations,
  getMockTripById,
  getMockTripMembers,
  getMockTripNotifications,
  removeMockTripMember,
  toggleMockChecklist,
  updateMockChecklist,
  updateMockTrip,
} from '../data/mock/trip-details.mock';
import {
  addMockNotification,
  deleteMockNotification,
  getMockUserNotifications,
} from '../data/mock/notifications.mock';

class ApiService {
  public api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      const storedToken = localStorage.getItem('token');
      const token = (storedToken && !storedToken.startsWith('mock.'))
        ? storedToken
        : 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MSwicGhvbmUiOiIwOTEyMTIzNDU2NyIsInJvbGUiOiJ1c2VyIiwiaWF0IjoxNzkyMzIxNjUwLCJleHAiOjE4MjM4NTc2NTB9.ZZBKK7UNZle4J_rVZHiFoOt3mAWEqtcH9QoX-S4Qz4M';
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  // Auth Methods
  async sendOtp(data: SendOtpRequest): Promise<OtpResponse> {
    if (isMockMode()) {
      console.log('Mock OTP for', data.phoneNumber, ': 123456');
      return { success: true, otp: '123456' };
    }
    console.log('Sending OTP request:', data);
    const response = await this.api.post('/auth/send-otp', data);
    console.log('OTP response:', response.data);
    return response.data;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    if (isMockMode()) {
      if (data.otp !== '123456') {
        throw new Error('کد تایید نامعتبر است');
      }
      const payload = btoa(JSON.stringify({ sub: '1', role: 'user' }));
      const accessToken = `mock.${payload}.dev`;
      return {
        accessToken,
        user: {
          id: '1',
          phoneNumber: data.phoneNumber,
          role: 'user',
          firstName: 'کاربر',
          lastName: 'تست',
        },
      };
    }
    const response = await this.api.post('/auth/login', data);
    return response.data;
  }

  // User Profile Methods
  async getProfile(): Promise<any> {
    try {
      const response = await this.api.get('/users/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  async updateProfile(profileData: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    gender?: 'male' | 'female';
    annualTravelBudget?: number;
    mbtiType?: string;
    travelStyles?: string[];
    interests?: string[];
    accommodationPreferences?: string[];
    travelPreferences?: string[];
  }): Promise<any> {
    try {
      const response = await this.api.put('/users/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // User Preferences Methods
  async getUserPreferences(): Promise<any> {
    try {
      const response = await this.api.get('/users/preferences');
      return response.data;
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      return null;
    }
  }

  async updateUserPreferences(preferences: {
    calendarType?: 'persian' | 'gregorian';
    language?: 'fa' | 'en';
    dateFormat?: string;
  }): Promise<any> {
    try {
      const response = await this.api.put('/users/preferences', preferences);
      return response.data;
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  }

  // Trip Methods
  async getTrips(): Promise<Trip[]> {
    if (isMockMode()) {
      return getMockTrips();
    }
    try {
      const response = await this.api.get('/trips');
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.data || []);
      return list.length > 0 ? list : getMockTrips();
    } catch (error) {
      console.warn('Failed to load trips from API, falling back to mock:', error);
      return getMockTrips();
    }
  }

  async getTripById(tripId: string): Promise<Trip> {
    if (isMockMode()) return getMockTripById(tripId);
    try {
      const response = await this.api.get(`/trips/${tripId}`);
      const data = response.data?.data || response.data;
      if (data && (data.id || data.title)) {
        return data;
      }
      return getMockTripById(tripId);
    } catch (error) {
      console.warn(`Failed to fetch trip ${tripId} from backend, falling back to mock:`, error);
      return getMockTripById(tripId);
    }
  }

  async createTrip(data: CreateTripData): Promise<Trip> {
    if (isMockMode()) return createMockTrip(data);
    const response = await this.api.post('/trips', data);
    return response.data;
  }

  async updateTrip(tripId: string, data: UpdateTripData): Promise<Trip> {
    if (isMockMode()) return updateMockTrip(tripId, data);
    const response = await this.api.put(`/trips/${tripId}`, data);
    return response.data;
  }

  async deleteTrip(tripId: string): Promise<void> {
    if (isMockMode()) return deleteMockTrip(tripId);
    await this.api.delete(`/trips/${tripId}`);
  }

  // Trip Member Methods
  async getTripMembers(tripId: string): Promise<any[]> {
    if (isMockMode()) return getMockTripMembers(tripId);
    try {
      const response = await this.api.get(`/trips/${tripId}/members`);
      const data = response.data;
      return Array.isArray(data) ? data : (data?.data || []);
    } catch {
      return [];
    }
  }

  async addTripMember(tripId: string, data: { phoneNumber: string; role: string }): Promise<void> {
    if (isMockMode()) return addMockTripMember(tripId, data);
    const response = await this.api.post(`/trips/${tripId}/members`, data);
    return response.data;
  }

  async removeTripMember(tripId: string, memberId: string): Promise<void> {
    if (isMockMode()) return removeMockTripMember(tripId, memberId);
    await this.api.delete(`/trips/${tripId}/members/${memberId}`);
  }

  // Checklist Methods
  async getChecklists(tripId: string): Promise<Checklist[]> {
    if (isMockMode()) return getMockChecklists(tripId);
    try {
      const response = await this.api.get(`/trips/${tripId}/checklists`);
      const data = response.data;
      return Array.isArray(data) ? data : (data?.data || []);
    } catch {
      return [];
    }
  }

  async createChecklist(tripId: string, data: CreateChecklistData): Promise<Checklist> {
    if (isMockMode()) return createMockChecklist(tripId, data);
    const response = await this.api.post(`/trips/${tripId}/checklists`, data);
    return response.data;
  }

  async toggleChecklist(tripId: string, checklistId: string, isCompleted: boolean): Promise<Checklist> {
    if (isMockMode()) return toggleMockChecklist(tripId, checklistId, isCompleted);
    const response = await this.api.put(`/trips/${tripId}/checklists/${checklistId}/toggle`, {
      isCompleted,
    });
    return response.data;
  }

  async updateChecklist(tripId: string, checklistId: string, data: Partial<Checklist>): Promise<Checklist> {
    if (isMockMode()) return updateMockChecklist(tripId, checklistId, data);
    const response = await this.api.patch(`/trips/${tripId}/checklists/${checklistId}`, data);
    return response.data;
  }

  async deleteChecklist(tripId: string, checklistId: string): Promise<void> {
    if (isMockMode()) return deleteMockChecklist(tripId, checklistId);
    await this.api.delete(`/trips/${tripId}/checklists/${checklistId}`);
  }

  // Expense Methods
  async getExpenses(tripId: string): Promise<Expense[]> {
    if (isMockMode()) return getMockExpenses(tripId);
    try {
      const response = await this.api.get(`/trips/${tripId}/expenses`);
      const data = response.data;
      return Array.isArray(data) ? data : (data?.data || []);
    } catch {
      return [];
    }
  }

  async createExpense(tripId: string, data: CreateExpenseData): Promise<Expense> {
    if (isMockMode()) return createMockExpense(tripId, data);
    const response = await this.api.post(`/trips/${tripId}/expenses`, {
      title: data.description, // تبدیل description به title
      amount: data.amount,
      participantIds: data.participantIds || [data.payerId || 'current-user'], // استفاده از participantIds یا payerId
      date: data.date || new Date().toISOString(), // استفاده از تاریخ ارسالی یا تاریخ فعلی
      description: data.description,
      payerId: data.payerId || 'current-user' // اضافه کردن payerId
    });
    return response.data;
  }

  async deleteExpense(tripId: string, expenseId: string): Promise<void> {
    if (isMockMode()) return deleteMockExpense(tripId, expenseId);
    await this.api.delete(`/trips/${tripId}/expenses/${expenseId}`);
  }

  // Announcement Methods
  async getAnnouncements(tripId: string): Promise<Announcement[]> {
    if (isMockMode()) return getMockAnnouncements(tripId);
    const response = await this.api.get(`/trips/${tripId}/announcements`);
    return response.data;
  }

  async createAnnouncement(tripId: string, content: string): Promise<Announcement> {
    if (isMockMode()) return createMockAnnouncement(tripId, content);
    const response = await this.api.post(`/trips/${tripId}/announcements`, {
      content,
    });
    return response.data;
  }

  async updateAnnouncement(tripId: string, announcementId: number, content: string): Promise<Announcement> {
    const response = await this.api.put(`/trips/${tripId}/announcements/${announcementId}`, {
      content,
    });
    return response.data;
  }

  async deleteAnnouncement(tripId: string, announcementId: number): Promise<void> {
    if (isMockMode()) return deleteMockAnnouncement(tripId, announcementId);
    await this.api.delete(`/trips/${tripId}/announcements/${announcementId}`);
  }

  // Location Methods
  async getLocations(tripId: string): Promise<Location[]> {
    if (isMockMode()) return getMockLocations(tripId);
    const response = await this.api.get(`/trips/${tripId}/locations`);
    return response.data;
  }

  async createLocation(tripId: string, name: string, address: string): Promise<Location> {
    if (isMockMode()) return createMockLocation(tripId, name, address);
    const response = await this.api.post(`/trips/${tripId}/locations`, {
      name,
      address,
    });
    return response.data;
  }

  async updateLocation(tripId: string, locationId: number, name: string, address: string): Promise<Location> {
    const response = await this.api.put(`/trips/${tripId}/locations/${locationId}`, {
      name,
      address,
    });
    return response.data;
  }

  async deleteLocation(tripId: string, locationId: number): Promise<void> {
    if (isMockMode()) return deleteMockLocation(tripId, locationId);
    await this.api.delete(`/trips/${tripId}/locations/${locationId}`);
  }

  // Trip Notification Methods (legacy - for trip-specific notifications)
  async getNotifications(tripId: string): Promise<Notification[]> {
    const response = await this.api.get(`/trips/${tripId}/notifications`);
    return response.data;
  }

  async createNotification(tripId: string, title: string, message: string, scheduledDate: string): Promise<Notification> {
    const response = await this.api.post(`/trips/${tripId}/notifications`, {
      title,
      message,
      scheduledDate,
    });
    return response.data;
  }

  async updateNotification(tripId: string, notificationId: number, message: string, scheduledDate: string): Promise<Notification> {
    const response = await this.api.put(`/trips/${tripId}/notifications/${notificationId}`, {
      message,
      scheduledDate,
    });
    return response.data;
  }

  async deleteNotification(tripId: string, notificationId: number): Promise<void> {
    await this.api.delete(`/trips/${tripId}/notifications/${notificationId}`);
  }

  // Notification Service Methods
  async getUserNotifications(userId: string, limit?: number): Promise<any[]> {
    if (isMockMode()) {
      return getMockUserNotifications(userId, limit ?? 50);
    }
    try {
      const response = await this.api.get(`/notifications/user/${userId}`, {
        params: limit ? { limit } : undefined,
      });
      const data = response.data;
      return Array.isArray(data) ? data : (data?.data || []);
    } catch (err) {
      console.warn('Failed to load notifications from API, using mock:', err);
      return getMockUserNotifications(userId, limit ?? 50);
    }
  }

  async getTripNotificationsFromService(tripId: string): Promise<any[]> {
    if (isMockMode()) return getMockTripNotifications(tripId);
    const response = await this.api.get(`/notifications/trip/${tripId}`);
    return response.data;
  }

  async createNotificationService(notificationData: {
    userId?: string;
    tripId?: string;
    type: 'sms' | 'email' | 'push' | 'in_app';
    recipient?: string;
    title?: string;
    message: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    scheduledAt?: string;
    templateId?: string;
    templateData?: Record<string, any>;
    metadata?: Record<string, any>;
  }): Promise<any> {
    if (isMockMode()) {
      return addMockNotification({
        type: notificationData.type,
        title: notificationData.title ?? 'اعلان جدید',
        message: notificationData.message,
        recipient: notificationData.recipient ?? '',
        status: 'pending',
        priority: notificationData.priority ?? 'medium',
        scheduledAt: notificationData.scheduledAt,
        tripId: notificationData.tripId,
      });
    }
    const response = await this.api.post('/notifications', notificationData);
    return response.data;
  }

  async deleteNotificationService(notificationId: string | number): Promise<void> {
    if (isMockMode()) {
      deleteMockNotification(Number(notificationId));
      return;
    }
    await this.api.delete(`/notifications/${notificationId}`);
  }

  async getNotificationLogs(notificationId: number): Promise<any[]> {
    const response = await this.api.get(`/notifications/${notificationId}/logs`);
    return response.data;
  }

  // Daily Plan Methods
  async getDailyPlans(tripId: string): Promise<DailyPlan[]> {
    if (isMockMode()) return getMockDailyPlans(tripId);
    const response = await this.api.get(`/trips/${tripId}/daily-plans`);
    return response.data;
  }

  async createDailyPlan(tripId: string, dayNumber: number, date: string, activities: string[]): Promise<DailyPlan> {
    if (isMockMode()) return createMockDailyPlan(tripId, dayNumber, date, activities);
    const response = await this.api.post(`/trips/${tripId}/daily-plans`, {
      dayNumber,
      date,
      activities,
    });
    return response.data;
  }

  async updateDailyPlan(tripId: string, dailyPlanId: number, dayNumber: number, date: string, activities: string[]): Promise<DailyPlan> {
    const response = await this.api.put(`/trips/${tripId}/daily-plans/${dailyPlanId}`, {
      dayNumber,
      date,
      activities,
    });
    return response.data;
  }

  async deleteDailyPlan(tripId: string, dailyPlanId: number): Promise<void> {
    if (isMockMode()) return deleteMockDailyPlan(tripId, dailyPlanId);
    await this.api.delete(`/trips/${tripId}/daily-plans/${dailyPlanId}`);
  }

  // Document Methods
  async getDocuments(tripId: string): Promise<Document[]> {
    if (isMockMode()) return getMockDocuments(tripId);
    const response = await this.api.get(`/trips/${tripId}/documents`);
    return response.data;
  }

  async createDocument(tripId: string, name: string, filePath: string, fileType: string, fileSize: number, uploadedByUserId: string): Promise<Document> {
    if (isMockMode()) return createMockDocument(tripId, name, filePath, fileType, fileSize, uploadedByUserId);
    const response = await this.api.post(`/trips/${tripId}/documents`, {
      name,
      filePath,
      fileType,
      fileSize,
      uploadedByUserId,
    });
    return response.data;
  }

  async updateDocument(tripId: string, documentId: number, name: string, filePath: string, fileType: string, fileSize: number): Promise<Document> {
    const response = await this.api.put(`/trips/${tripId}/documents/${documentId}`, {
      name,
      filePath,
      fileType,
      fileSize,
    });
    return response.data;
  }

  async deleteDocument(tripId: string, documentId: number): Promise<void> {
    if (isMockMode()) return deleteMockDocument(tripId, documentId);
    await this.api.delete(`/trips/${tripId}/documents/${documentId}`);
  }
}

export const apiService = new ApiService();
